<template>
  <v-card
    class="person-card elevation-0"
    :class="{ 'person-card--deleted': person.isDeleted, 'person-card--inactive': !person.isActive && !person.isDeleted }"
    height="100%"
  >
    <!-- Header -->
    <v-card-title class="subtitle-1 font-weight-bold pb-0">
      <v-col>
        <v-row>
          <!-- Person Name -->
          <v-col
            class="pb-2 col-grow text-truncate clickable"
            :title="person.personName"
            @click="$emit('view-profile', person)"
          >
            <span class="person-name">
              {{ person.personName || 'Unknown' }}
            </span>
          </v-col>

          <!-- Status Chip -->
          <v-col class="col-auto pb-2 px-0 text-right">
            <v-chip
              :color="statusColor"
              :class="`ml-2 ${statusTextColor}--text`"
              small
            >
              {{ statusLabel }}
            </v-chip>
          </v-col>

          <v-col class="col-auto pb-2 pt-2 px-0">
            <!-- Actions Menu -->
            <v-menu
              offset-y
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'ellipsis-vertical']"
                    size="lg"
                  />
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="$emit('view-profile', person)">
                  <v-list-item-title>View</v-list-item-title>
                </v-list-item>

                <v-list-item @click="$emit('copy-person', person)">
                  <v-list-item-title>Copy</v-list-item-title>
                </v-list-item>

                <template v-if="isAdmin">
                  <v-divider />
                  <v-list-item @click="$emit('edit', person)">
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="$emit('delete', person)">
                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <!-- Email Row -->
        <v-row class="caption mt-1">
          <v-col class="col-auto pt-0 pb-1">
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              class="mr-1 midgray--text"
              aria-label="Email"
            />
            <span class="person-email ml-1">{{ person.email || 'N/A' }}</span>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col class="py-0">
            <v-divider />
          </v-col>
        </v-row>
      </v-col>
    </v-card-title>

    <!-- Card Content -->
    <v-card-text>
      <v-col class="pt-4">
        <!-- Type & Access Row -->
        <v-row>
          <v-col class="col-auto py-0 pr-2">
            <!-- Type Chip -->
            <v-chip
              small
              outlined
              :color="person.isInternal ? 'primary' : 'bluegray'"
              class="mr-0 mt-2 rounded-pill"
            >
              <font-awesome-icon
                :icon="person.isInternal ? ['fas', 'user-tie'] : ['fas', 'user']"
                aria-hidden="true"
              />
              <span class="ml-1 chip-label">{{ person.isInternal ? 'Internal' : 'External' }}</span>
            </v-chip>
          </v-col>

          <v-col
            v-if="person.access && person.access.level"
            class="col-auto py-0 pl-0"
          >
            <!-- Level Chip -->
            <v-chip
              small
              outlined
              color="bluegray"
              class="mt-2 rounded-pill"
            >
              <font-awesome-icon
                :icon="['fas', 'layer-group']"
                aria-hidden="true"
              />
              <span class="ml-1 chip-label">{{ levelLabel }}</span>
            </v-chip>
          </v-col>

          <v-col
            v-if="!person.isConfigValid"
            class="col-auto py-0 pl-0"
          >
            <!-- Config Invalid Chip -->
            <v-chip
              small
              outlined
              color="toasted"
              class="mt-2 rounded-pill"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                aria-hidden="true"
              />
              <span class="ml-1 chip-label">Invalid Config</span>
            </v-chip>
          </v-col>

          <v-col
            v-if="person.isAdmin"
            class="col-auto py-0 pl-0"
          >
            <!-- Admin Badge -->
            <v-chip
              small
              outlined
              color="primary"
              class="mt-2 rounded-pill"
            >
              <font-awesome-icon
                :icon="['fas', 'user-shield']"
                aria-hidden="true"
              />
              <span class="ml-1 chip-label">Admin</span>
            </v-chip>
          </v-col>
        </v-row>

        <!-- Details Section -->
        <v-row>
          <v-col>
            <!-- GAM Verified -->
            <div
              v-if="person.isInternal && person.isGamVerifiedAndActive"
              class="person-detail-row"
            >
              <span class="detail-label">GAM</span>
              <span class="detail-value">
                <font-awesome-icon
                  :icon="['fas', 'check-circle']"
                  class="grassgreen--text mr-1"
                  size="sm"
                />
                Verified
              </span>
            </div>

            <!-- Team -->
            <div
              v-if="person.isInternal && person.teamName"
              class="person-detail-row"
            >
              <span class="detail-label">Team</span>
              <span class="detail-value">{{ person.teamName }}</span>
            </div>

            <!-- Manager -->
            <div
              v-if="person.isInternal && person.managerPersonName"
              class="person-detail-row"
            >
              <span class="detail-label">Manager</span>
              <span class="detail-value">{{ person.managerPersonName }}</span>
            </div>

            <!-- Access Errors -->
            <div
              v-if="hasAccessErrors"
              class="person-detail-row"
            >
              <span class="detail-label">Errors</span>
              <span class="detail-value">
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="toasted--text mr-1"
                  size="sm"
                />
                {{ accessErrorCount }} {{ accessErrorCount === 1 ? 'error' : 'errors' }}
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Reports Access -->
        <v-row
          v-if="hasReportAccess"
          class="mt-2"
        >
          <v-col>
            <v-divider class="mb-3" />

            <div class="detail-label mb-2">
              Reports
            </div>
            <div class="report-icons">
              <v-tooltip
                v-if="hasPacing"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'chart-line']"
                    class="grassgreen--text"
                    size="sm"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Pacing</span>
              </v-tooltip>

              <v-tooltip
                v-if="hasDiscrepancy"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'chart-bar']"
                    class="grassgreen--text"
                    size="sm"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Discrepancy</span>
              </v-tooltip>

              <v-tooltip
                v-if="hasHealthNetwork"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'network-wired']"
                    class="grassgreen--text"
                    size="sm"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Network Health</span>
              </v-tooltip>

              <v-tooltip
                v-if="hasHealthAdx"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'heartbeat']"
                    class="grassgreen--text"
                    size="sm"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>AdX Health</span>
              </v-tooltip>

              <v-tooltip
                v-if="hasYieldPartner"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <font-awesome-icon
                    :icon="['fas', 'handshake']"
                    class="grassgreen--text"
                    size="sm"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span>Yield Partner</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>

    <v-divider />

    <v-card-actions class="person-card-footer px-4 py-2">
      <v-tooltip
        bottom
        max-width="360"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            class="d-inline-flex align-center access-indicator"
            v-bind="attrs"
            v-on="on"
          >
            <font-awesome-icon
              :icon="accessIndicatorIcon"
              :class="`${accessIndicatorColor}--text mr-2`"
              size="sm"
            />
            <span class="caption font-weight-medium">{{ accessIndicatorLabel }}</span>
          </div>
        </template>

        <div class="caption">
          <div
            v-for="(reason, index) in accessReasonSummary"
            :key="`${person.personId || person.email || 'person'}-tooltip-reason-${index}`"
            class="mb-1"
          >
            {{ reason }}
          </div>
        </div>
      </v-tooltip>

      <v-spacer />

      <span class="caption coolgray--text">
        {{ accessErrorCount }} {{ accessErrorCount === 1 ? 'issue' : 'issues' }}
      </span>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: 'PersonCard',
  props: {
    person: {
      type: Object,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    statusLabel() {
      if (this.person.isDeleted) return 'Deleted'
      if (this.person.isActive) return 'Active'
      return 'Inactive'
    },

    /**
     * Person status color using color psychology:
     * - Green (retrogreendark): Active/engaged
     * - Gray (midgray): Inactive/neutral
     * - Red (toasted): Deleted/alert
     */
    statusColor() {
      if (this.person.isDeleted) return 'toasted'
      if (this.person.isActive) return 'retrogreendark'
      return 'midgray'
    },

    statusTextColor() {
      return 'white'
    },

    levelLabel() {
      const level = this.person.access && this.person.access.level

      if (!level) return ''

      return level.charAt(0).toUpperCase() + level.slice(1)
    },

    hasAccessErrors() {
      return this.person.hasAccessErrors && this.person.hasAccessErrors.length > 0
    },

    accessErrorCount() {
      return this.hasAccessErrors ? this.person.hasAccessErrors.length : 0
    },

    accessIndicatorIcon() {
      return this.person.canAccessApps === true ? ['fas', 'circle-check'] : ['fas', 'triangle-exclamation']
    },

    accessIndicatorColor() {
      return this.person.canAccessApps === true ? 'grassgreen' : 'toasted'
    },

    accessIndicatorLabel() {
      return this.person.canAccessApps === true ? 'Access Checks Passed' : 'Access Needs Attention'
    },

    hasAuthErrors() {
      return this.person.hasAuthErrors && this.person.hasAuthErrors.length > 0
    },

    accessReasonSummary() {
      if (this.person.canAccessApps === true) {
        return ['All access checks passed']
      }

      const reasons = []
      const addReason = (reason) => {
        if (!reason || reasons.includes(reason)) return
        reasons.push(reason)
      }

      if (this.hasAccessErrors) {
        this.person.hasAccessErrors.slice(0, 2).forEach((reason) => addReason(this.formatAccessReason(reason)))
      }

      if (this.hasAuthErrors) {
        this.person.hasAuthErrors.slice(0, 2).forEach((reason) => addReason(this.formatAccessReason(reason)))
      }

      if (!this.hasAccessErrors && !this.hasAuthErrors) {
        if (this.person.isPersonAuthorized === false) addReason('Person is inactive or deleted')
        if (this.person.isUserAuthorized === false) addReason('User is inactive or expired')
        if (this.person.isConfigValid === false) addReason('Config validation failed')
      }

      return reasons.length > 0 ? reasons.slice(0, 2) : ['Access denied: no reason details available']
    },

    hasPacing() {
      if (!this.person.network || !Array.isArray(this.person.network)) return false
      return this.person.network.some((row) => row.publisher && row.publisher.hasPacing)
    },

    hasDiscrepancy() {
      if (!this.person.network || !Array.isArray(this.person.network)) return false
      return this.person.network.some((row) => row.publisher && row.publisher.hasDiscrepancy)
    },

    hasHealthNetwork() {
      if (!this.person.network || !Array.isArray(this.person.network)) return false
      return this.person.network.some((row) => row.hasProgrammatic && row.publisher && row.publisher.hasHealthNetwork)
    },

    hasHealthAdx() {
      if (!this.person.network || !Array.isArray(this.person.network)) return false
      return this.person.network.some((row) => row.hasProgrammatic && row.publisher && row.publisher.hasHealthAdx)
    },

    hasYieldPartner() {
      if (!this.person.network || !Array.isArray(this.person.network)) return false
      return this.person.network.some((row) => row.hasProgrammatic && row.publisher && row.publisher.hasYieldPartner)
    },

    hasReportAccess() {
      return this.hasPacing || this.hasDiscrepancy || this.hasHealthNetwork || this.hasHealthAdx || this.hasYieldPartner
    },
  },
  methods: {
    formatAccessReason(reason) {
      if (typeof reason !== 'string') return String(reason || '')

      return reason
        .replace(/[_/]+/g, ' ')
        .replace(/:/g, ': ')
        .replace(/\s+/g, ' ')
        .trim()
    },
  },
}
</script>

<style scoped lang="scss">
.chip-label {
  font-weight: 700 !important;
  letter-spacing: 0.1px;
}

.person-card {
  transition: box-shadow 0.2s ease, transform 0.1s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
  }

  &--deleted {
    opacity: 0.7;
    border-left: 3px solid var(--v-error-base);
  }

  &--inactive {
    opacity: 0.85;
  }

  .clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .col-grow {
    flex-grow: 1;
    min-width: 0;
  }

  .person-card-footer {
    min-height: 38px;
  }

  .access-indicator {
    cursor: help;
  }

  .v-card__title {
    .v-row {
      margin-left: -8px;
      margin-right: -8px;
    }
  }

  .v-card__text {
    flex-grow: 1;
  }
}

.person-name {
  font-weight: 700;
  color: var(--v-darkblue-base);
}

.person-email {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .theme--dark & {
    color: rgba(255, 255, 255, 0.7);
  }
}

.person-detail-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-midgray-base);
  min-width: 80px;
}

.detail-value {
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.detail-value--stacked {
  align-items: flex-start;
  flex-direction: column;
}

.detail-value-line {
  line-height: 1.2;
}

.report-icons {
  gap: 12px;
  display: flex;
  align-items: center;
}

// Dark mode support
.theme--dark {
  .person-card {
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }

    &--deleted {
      border-left-color: var(--v-error-lighten1);
    }
  }

  .person-name {
    color: var(--v-gray-base);
  }
}
</style>
